import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import styles from '../SerialCreditApplication.module.scss'

interface ResultData {
  status: 'success' | 'fail' | 'pending'
  orderId: string
  authCode?: string
  refNum?: string
  amount?: number
  errorMessage?: string
}

/**
 * Serial Credit Result Page
 * Backend'den redirect ile gelen sonucu gösterir (fallback sayfa)
 *
 * URL Parametreleri:
 * - status: 'success' | 'fail' | 'pending'
 * - orderId: Başvuru numarası
 * - authCode: Onay kodu (success için)
 * - refNum: Referans numarası (success için)
 * - amount: Tutar (success için)
 * - error: Hata mesajı (fail için)
 * - message: Bilgi mesajı (pending / çözümlenmiş sonuç için)
 */
function SerialCreditResult() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [resultData, setResultData] = useState<ResultData | null>(null)

  useEffect(() => {
    const status = searchParams.get('status') as 'success' | 'fail' | 'pending'
    const orderId = searchParams.get('orderId') || ''
    const authCode = searchParams.get('authCode') || undefined
    const refNum = searchParams.get('refNum') || undefined
    const amount = searchParams.get('amount') ? parseFloat(searchParams.get('amount')!) : undefined
    const errorMessage = searchParams.get('error') || searchParams.get('message') || undefined

    if (!status || !orderId) {
      Swal.fire({
        icon: 'error',
        title: 'Geçersiz İstek',
        text: 'Geçersiz sonuç parametreleri.',
        confirmButtonText: 'Tamam',
      }).then(() => {
        navigate('/myspecial/serial-credit-application')
      })
      return
    }

    setResultData({
      status,
      orderId,
      authCode,
      refNum,
      amount,
      errorMessage,
    })
  }, [searchParams, navigate])

  const handleNewApplication = () => {
    navigate('/myspecial/serial-credit-application')
  }

  const formatCurrency = (amount: number, currency: string = 'TL'): string => {
    const formattedNumber = amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return `${formattedNumber} ${currency}`
  }

  if (!resultData) {
    return (
      <div className={styles.serialCreditApplication}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.processingSection}>
              <p>Yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (resultData.status === 'success') {
    return (
      <div className={styles.serialCreditApplication}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.resultSection}>
              <div className={`${styles.iconWrapper} ${styles.success}`}>
                <i className='fas fa-check-circle'></i>
              </div>
              <h3>Kredi Başvurusu Onaylandı!</h3>
              <p>Müşterinizin kredi başvurusu başarıyla tamamlandı.</p>

              {/* Makbuz Bilgileri */}
              <div className={styles.receiptCard}>
                <div className={styles.receiptHeader}>
                  <i className='fas fa-receipt'></i>
                  <span>İşlem Makbuzu</span>
                </div>
                <div className={styles.receiptBody}>
                  <div className={styles.receiptRow}>
                    <span className={styles.receiptLabel}>Başvuru No:</span>
                    <span className={styles.receiptValue}>{resultData.orderId}</span>
                  </div>
                  {resultData.authCode && (
                    <div className={styles.receiptRow}>
                      <span className={styles.receiptLabel}>Onay Kodu:</span>
                      <span className={styles.receiptValue}>{resultData.authCode}</span>
                    </div>
                  )}
                  {resultData.refNum && (
                    <div className={styles.receiptRow}>
                      <span className={styles.receiptLabel}>Referans No:</span>
                      <span className={styles.receiptValue}>{resultData.refNum}</span>
                    </div>
                  )}
                  {resultData.amount && (
                    <div className={styles.receiptRow}>
                      <span className={styles.receiptLabel}>Tutar:</span>
                      <span className={styles.receiptValue} style={{ color: '#28a745', fontWeight: 'bold' }}>
                        {formatCurrency(resultData.amount, 'TL')}
                      </span>
                    </div>
                  )}
                </div>
                <div className={styles.receiptFooter}>
                  <i className='fas fa-clock'></i>
                  <span>{new Date().toLocaleString('tr-TR')}</span>
                </div>
              </div>

              <div className={styles.buttonGroup}>
                <button className={styles.btnPrimary} onClick={handleNewApplication}>
                  <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
                  Yeni Başvuru
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pending durumu (yeni müşteri akışı - banka onboarding devam ediyor)
  if (resultData.status === 'pending') {
    return (
      <div className={styles.serialCreditApplication}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.resultSection}>
              <div className={`${styles.iconWrapper} ${styles.pending}`}>
                <i className='fas fa-hourglass-half'></i>
              </div>
              <h3>Kredi Başvurunuz Değerlendiriliyor</h3>
              <p>
                Müşterinizin banka onboarding süreci (video bankacılık veya kurye ile müşteri olma adımı)
                tamamlandığında başvuru sonuçlanacaktır. Bu aşamada kredi henüz kullandırılmamıştır; lütfen ürün
                teslimi yapmayınız. Sonuç bilgisi tarafınıza iletilecek ve kredi panelinden takip edilebilecektir.
              </p>

              <div className={styles.applicationId}>
                Başvuru No: <strong>{resultData.orderId}</strong>
              </div>

              {resultData.errorMessage && (
                <div className={styles.errorDetail}>
                  <i className='fas fa-info-circle'></i>
                  <span>{resultData.errorMessage}</span>
                </div>
              )}

              <div className={styles.buttonGroup}>
                <button className={styles.btnPrimary} onClick={handleNewApplication}>
                  <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
                  Yeni Başvuru
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Fail durumu
  return (
    <div className={styles.serialCreditApplication}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.resultSection}>
            <div className={`${styles.iconWrapper} ${styles.error}`}>
              <i className='fas fa-times-circle'></i>
            </div>
            <h3>Kredi Başvurusu Reddedildi</h3>
            <p>Müşterinizin kredi başvurusu onaylanmadı.</p>

            <div className={styles.applicationId}>
              Başvuru No: <strong>{resultData.orderId}</strong>
            </div>

            {resultData.errorMessage && (
              <div className={styles.errorDetail}>
                <i className='fas fa-exclamation-triangle'></i>
                <span>{resultData.errorMessage}</span>
              </div>
            )}

            <div className={styles.buttonGroup}>
              <button className={styles.btnPrimary} onClick={handleNewApplication}>
                <i className='fas fa-plus' style={{ marginRight: '8px' }}></i>
                Yeni Başvuru
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SerialCreditResult
